/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import _, {invert} from 'lodash';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {error} from '@ion/core/src/logger/logger';
import {loadHeartBeatInfo} from '@ion/core/src/services/Connect';
import {translate} from '../locale/locale';
import {
  Device,
  DeviceFiles,
  GenericObject,
  Group,
  IdReport
} from '../types/entities';
import {DeviceSortTypes} from '../types/services';
import {parseValueWithKey} from './StringUtil';

export enum MDTCategories {
  MDT = 'MDT',
  CAMERA = 'Camera'
};

export enum LMUCategories {
  LMU = 'LMU',
  LMU_TTU = 'LMU_TTU'
};

export enum DevicePlatforms {
  FIRMWARE = 'LMU Firmware',
  CONFIG = 'LMU Config',
  PEG2 = 'LMU PEG2'
};

export enum VbusFirmwareMode {
  JPOD = 'JPOD',
  OBD = 'OBD',
  VBU = 'VBU'
};

type PlatformValidator = (device: Device, platformId: number) => boolean;

type PendingDataArgs = {
  device: Device;
  validPlatformIds: number[];
  isVbuFwEnabled?: boolean;
  isPlatformValidCallback?: PlatformValidator | null;
};

const VALUES = ['OBD_TYPE_2', 'JPOD2'];

/**
 * @function getSignalStrength
 *
 * Takes a standard RSSI number (from -26 to -100) and returns
 * a signalStrength number from 0 to 4
 */
export const getSignalStrength = (signal) => {
  if (signal >= -67) return 4;
  if (signal >= -70 && signal < -67) return 3;
  if (signal >= -80 && signal < -70) return 2;
  if (signal >= -90 && signal < -80) return 1;
  return 0;
};

/**
 * Determines if the provided device is an MDT (Mobile Device Tablet) device.
 * @param device the device to evaluate (containing a category)
 * @returns if the provided device is an MDT device
 */
export const isMDTDevice = (device: Device = {}): boolean => {
  const category = device?.category;
  if (category) {
    return !!MDTCategories[(Array.isArray(category) ? category?.[0] : category)];
  }
  return false;
};

/**
 * Determines if the provided device is an LMU device.
 * @param device the device to evaluate (containing a category)
 * @returns if the provided device is an LMU device
 */
export const isLMUDevice = (device: Device = {}): boolean => {
  const category = device?.category;
  if (category) {
    return !!LMUCategories[(Array.isArray(category) ? category?.[0] : category)];
  }
  return false;
};

/**
 * Given a populated device, determines the respective identifier based on the device's type fields (such as category &
 * architecture) to return identifiers (such as esn, serial number, etc).
 *
 * If the requested identifier is unavailable or not present on the given device it returns an empty string.
 *
 * @param device The device to determine the identifier of.
 * @returns A string device identifer.
 */
export const getDeviceIdentifierFromType = (device: Device = {}): string => {
  let identifier;
  if (isMDTDevice(device)) {
    identifier = device?.serialNumber;
  } else {
    identifier = device?.esn;
  }
  return identifier || '';
};

//  Parses a device and returns the id report's vin information.
export const getIdReportVin = (device: Device, defaultValue = ''): string => {
  const vin = getDeviceIdReport(device)?.extensions?.['VIN-INFO']?.[0];
  if (vin) {
    return parseValueWithKey({source: vin, key: 'VIN=', endingDelimiter: ',', customValueLength: 17});
  }
  return defaultValue;
};

// Given a device, will return the inner id report if available. Otherwise undefined.
export const getDeviceIdReport = (device: Device): IdReport | undefined => device?.idReport?.device?.idReport;

export const PEG2_SCRIPT_NAME_EXTENSION = '.pg2';

const REGULAR_NUMBER_REGEX      = /^\d+$/;
const IP_ADDRESS_REGEX          = /^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/;
const PEG2_SCRIPT_VERSION_REGEX = /^\d{1,3}\.\d{1,3}$/;
const PEG2_HOSTNAME_REGEX       = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)$/;
const PEG2_PORT_REGEX           = /^\d{1,5}$/;
const PEG2_INVALID_EXTENSION    = /\.[^.\s]+$/;

/**
 * Determines if the given esn is valid.
 *
 * @param esn the esn to validate
 * @returns true if valid, false otherwise
 */
export const isValidESN = (esn: string) => REGULAR_NUMBER_REGEX.test(esn);

/**
 * Determines if the given ipAddress is valid.
 *
 * @param ipAddress the ipAddress to validate
 * @returns true if valid, false otherwise
 */
export const isValidIPAddress = (ipAddress: string) => IP_ADDRESS_REGEX.test(ipAddress);

/**
 * Determines if the given hostname is a valid single-label hostname (no
 * dots, e.g. myhost, not myhost.example.com).
 *
 * @param hostname the hostname to validate
 * @returns true if valid, false otherwise
 */
export const isValidHostname = (hostname: string) => PEG2_HOSTNAME_REGEX.test(hostname);

/**
 * Determines if the given port is a valid TCP/UDP port number (1-65535).
 *
 * @param port the port to validate
 * @returns true if valid, false otherwise
 */
export const isValidPort = (port: string): boolean => {
  if (!PEG2_PORT_REGEX.test(port)) {
    return false;
  }
  const value = Number(port);
  return value >= 1 && value <= 65535;
};

/**
 * Determines if the given version is a valid varsion.
 * Accept only numeric value in NNN.NNN format.
 * (1-255.0-255, e.g. \"1.0\"). Max value is 255.255. 
 * @param version the version to validate
 * @returns true if valid, false otherwise
 */
export const isValidScriptVersion = (version: string): boolean => {
  if (!PEG2_SCRIPT_VERSION_REGEX.test(version)) {
    return false;
  }
  const [major, minor] = version.split('.').map(Number);
  return major >= 1 && major <= 255 && minor >= 0 && minor <= 255;
};

/**
 * Ensures that a script name has the required script extension. If the
 * provided script name already ends with '.pg2', it's returned unchanged.
 * If it ends with any other extension (e.g. '.txt'), that extension is
 * replaced with '.pg2'. If it has no extension at all, '.pg2' is appended.
 * @param name script name to validate
 * @returns A valid script name with the required extension
 */
export const getScriptNameWithExtension = (name: string): string => {
  const scriptName = name.trim();
  if (scriptName.endsWith(PEG2_SCRIPT_NAME_EXTENSION)) {
    return scriptName;
  }
  const baseName = scriptName.replace(PEG2_INVALID_EXTENSION, '');
  return `${baseName}${PEG2_SCRIPT_NAME_EXTENSION}`;
};

/**
 * Checks if a string (contained in an array) is a valid parameter to sort
 * devices by.
 * 
 * ```typescript
 * 
 *   import {isDeviceSortParamValid} from '@ion/core/src/utils/DeviceUtils';
 * 
 *   const isSortParamValid = isDeviceSortParamValid(sortParam);
 * 
 * ```
 * 
 * @param sortParam An array containing a single element, the string to check.
 */
export const isDeviceSortParamValid = (sort: string[]): boolean => {
  const sortParams = invert(DeviceSortTypes);
  return Boolean(sortParams[sort as unknown as DeviceSortTypes]);
};

export const PLATFORM_IDS = {
  firmware                         : 365,
  config                           : 366,
  peg2                             : 373,
  jpodFirmware                     : 374,
  jpodConfig                       : 375,
  hostedAppFirmware                : 381,
  hostedAppConfig                  : 382,
  hostedAppFirmwareFirstPeripheral : 383,
  hostedAppConfigFirstPeripheral   : 384,
  hostedAppFirmware2ndPeripheral   : 385,
  hostedAppConfig2ndPeripheral     : 386,
  hostedAppV2Firmware              : 387,
  hostedAppApnList                 : 388,
  hostedAppV3Firmware              : 389,
  hostedAppV4Firmware              : 390,
  hostedAppV5Firmware              : 391,
  hostedAppV6Firmware              : 392,
  edgeAppFirmware                  : 825,
  jpod2Firmware                    : 399,
  jpod2Config                      : 761,
  ble3Firmware                     : 420,
  vbu3Firmware                     : 419,
  bspModemFirmware                 : 767,
  cellRadioFirmware                : 405,
  obd2Type2Firmware                : 393,
  obd1Type1Firmware                : 377,
  obd2Type2DatabaseObd2            : 394,
  androidMdtFirmware               : 396
};

/**
 * Helper function for checking if a given platform is valid for a device's type.
 * 
 * ```typescript
 * 
 *   return isPlatformValidForDeviceMode(device, platform, {
 *     [VbusFirmwareMode.JPOD] : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
 *     [VbusFirmwareMode.OBD]  : [PLATFORM_IDS.obd1Type1Firmware, PLATFORM_IDS.obd2Type2Firmware],
 *     [VbusFirmwareMode.VBU]  : [PLATFORM_IDS.vbu3Firmware]
 *   });
 * 
 * ```
 * 
 * @param device         The device to get type/mode from.
 * @param platform       The platform to check.
 * @param validPlatforms An object containing device modes mapped to arrays 
 *        of their valid platform IDs.
 */
const isPlatformValidForDeviceMode = (
  device,
  platform,
  validPlatforms: {[key: string]: number[]}
): boolean => {
  const deviceMode = device?.extensions?.vbusMode;
  for (const mode in validPlatforms) {
    if (isEqual(deviceMode, mode)) {
      return validPlatforms[mode].includes(platform);
    }
  }
  return false;
};

/**
 * Based on the given device's type, will return whether the given platform is a valid for a JPOD/OBD/VBU file.
 */
export const isVbusFirmwarePlatformValid = (
  device: Device,
  platform: number
): boolean => {
  return isPlatformValidForDeviceMode(device, platform, {
    [VbusFirmwareMode.JPOD] : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
    [VbusFirmwareMode.OBD]  : [PLATFORM_IDS.obd1Type1Firmware, PLATFORM_IDS.obd2Type2Firmware],
    [VbusFirmwareMode.VBU]  : [PLATFORM_IDS.vbu3Firmware]
  });
};

/**
 * Based on the given device's type, will return whether the given platform is valid for a JPOD file.
 */
export const isJpodConfigFirmwarePlatformValid = (
  device: Device,
  platform: number
): boolean => {
  return isPlatformValidForDeviceMode(device, platform, {
    [VbusFirmwareMode.JPOD] : [PLATFORM_IDS.jpodConfig, PLATFORM_IDS.jpod2Config]
  });
};

/**
 * Gets pending data from a device.
 * 
 * ```typescript
 * 
 *   // Does not use `isPlatformValidCallback`
 *   return getPendingData({
 *     device,
 *     validPlatformIds : [PLATFORM_IDS.firmware]
 *   });
 * 
 *   // Uses `isPlatformValidCallback`
 *   return getPendingData({
 *     device,
 *     validPlatformIds        : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
 *     isVbuFwEnabled,
 *     isPlatformValidCallback : isVbusFirmwarePlatformValid
 *   });
 * 
 * ```
 * 
 * @param   param0                  Object to be destructured.
 * @param   device                  Device to get pending data from.
 * @param   validPlatformIds        Array of platform IDs that should be 
 *          included in the result.
 * @param   isVbuFwEnabled          Whether to use the callback or check
 *          against `validPlatformIds`. Defaults to false.
 * @param   isPlatformValidCallback Pass this in if `isVbuFwEnabled` could be
 *          true.
 * @returns Each pending item's `newVersion` appended with its `jobId`.
 */
const getPendingData = ({
  device,
  validPlatformIds,
  isVbuFwEnabled = false,
  isPlatformValidCallback = null
}: PendingDataArgs): string[] => {
  if (!device?.fileHistoryPending) {
    return [];
  }
  const filtered = (device.fileHistoryPending as {
    platformId: number;
    newVersion: string;
    jobId: string | number;
  }[]).filter(({platformId}) => {
    if (isVbuFwEnabled && isPlatformValidCallback) {
      return isPlatformValidCallback(device, platformId);
    }
    return validPlatformIds.includes(platformId);
  });
  return filtered.map(result => `[${result.newVersion}] ${result.jobId}`);
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Script data
*/
export const getPendingScript = (device: Device) => {
  return getPendingData({
    device,
    validPlatformIds : [PLATFORM_IDS.config, PLATFORM_IDS.peg2]
  });
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Firmware data
*/
export const getPendingFirmware = (device: Device) => {
  return getPendingData({
    device,
    validPlatformIds : [PLATFORM_IDS.firmware]
  });
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending JPOD Firmware data
*/
export const getPendingJpodFW = (device: Device, isVbuFwEnabled: boolean = false) => {
  return getPendingData({
    device,
    validPlatformIds        : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
    isVbuFwEnabled,
    isPlatformValidCallback : isVbusFirmwarePlatformValid
  });
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending JPOD Config data
*/
export const getPendingJpodConfig = (device: Device, isVbuFwEnabled: boolean = false) => {
  return getPendingData({
    device,
    validPlatformIds        : [PLATFORM_IDS.jpodConfig, PLATFORM_IDS.jpod2Config],
    isVbuFwEnabled,
    isPlatformValidCallback : isJpodConfigFirmwarePlatformValid
  });
};

/** Given a device, will return newVersion based on platformID,
    appended with jobID to display Pending Hosted App/ EdgeApp data
*/
export const getPendingEdgeHosted = (device: Device) => {
  return getPendingData({
    device,
    validPlatformIds : [
      PLATFORM_IDS.hostedAppFirmware,
      PLATFORM_IDS.hostedAppConfig,
      PLATFORM_IDS.hostedAppV2Firmware,
      PLATFORM_IDS.hostedAppApnList,
      PLATFORM_IDS.hostedAppV3Firmware,
      PLATFORM_IDS.hostedAppV4Firmware,
      PLATFORM_IDS.hostedAppV5Firmware,
      PLATFORM_IDS.hostedAppV6Firmware,
      PLATFORM_IDS.hostedAppFirmwareFirstPeripheral,
      PLATFORM_IDS.hostedAppConfigFirstPeripheral,
      PLATFORM_IDS.hostedAppFirmware2ndPeripheral,
      PLATFORM_IDS.hostedAppConfig2ndPeripheral,
      PLATFORM_IDS.edgeAppFirmware
    ]
  });
};

/**
 * Given a device, will return the file version based on the
 * platform id.
 */
export const getRadioFirmware = (device: Device) => {
  return getDeviceFileVersionsHelper({
    device,
    validPlatformIds : [PLATFORM_IDS.bspModemFirmware, PLATFORM_IDS.cellRadioFirmware]
  });
};

/**
* @deprecated
* @function getVbusMode
* Given a device, will return OBD or JPOD based on otaCapability
*  to display VBUS Mode
*/
export const getVbusMode = (device: Device) => {
  const vbusModeType = device?.idReport?.device?.idReport?.otaCapability?.protocolMap;
  if (vbusModeType) {
    const arr = Object.keys(vbusModeType);
    const checkOBD = arr.filter(str => (str.includes('OBD')));
    const checkJPOD = arr.filter(str => (str.includes('JPOD')));
    const checkBothTypes = VALUES.every(value => {
      return arr.includes(value);
    });
    if (checkBothTypes) {
      return 'OBD , JPOD';
    }
    if (checkOBD.length !== 0) {
      return 'OBD';
    }
    if (checkJPOD.length !== 0) {
      return 'JPOD';
    }
  }
  return '';
};

/**
 * Helper function for getting the versions of a JPOD device's files.
 * 
 * ```typescript
 * 
 *   return getDeviceFileVersionsHelper({
 *     device,
 *     getFirmwareCallback : isVbusFirmwarePlatformValid,
 *     validPlatformIds    : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
 *     isVbuFwEnabled
 *   });
 * 
 * ```
 * 
 * @param param0                  Object to be destructured.
 * @param device                  Device to get data from.
 * @param validPlatformIds        Array of platform IDs to match against each
 *        file version's platform for validity.
 * @param isVbuFwEnabled          When true, uses `isPlatformValidCallback`.
 *        When false, checks `validPlatformIds`. Defaults to false.
 * @param isPlatformValidCallback Callback that checks if each file version's
 *        platform is valid for the device's type.
 */
const getDeviceFileVersionsHelper = ({device, validPlatformIds, isVbuFwEnabled = false, isPlatformValidCallback = null}:
{device: Device, validPlatformIds: any[],
  isVbuFwEnabled?: boolean, isPlatformValidCallback?: (device, platform) => boolean}) => {
  const fileVersions = device?.extensions?.fileVersions;
  if ((_.isUndefined(fileVersions)) || (!Array.isArray(fileVersions))) {
    return '';
  }
  if (fileVersions) {
    return fileVersions.filter(({platform}) =>
      isVbuFwEnabled
        ? isPlatformValidCallback(device, platform)
        : validPlatformIds.includes(platform))?.map?.(result => result.version);
  }
};

export const getVersionByPlatformId = (device: Device, platformId) => {
  return getDeviceFileVersionsHelper({device, validPlatformIds: [platformId]});
};

/**
 * Given a device, will return current version based on platformID,
 * and display VBUS DB data
 */
export const getVbusDatabase = (device: Device) => {
  return getDeviceFileVersionsHelper({device, validPlatformIds: [PLATFORM_IDS.obd2Type2DatabaseObd2]});
};

/** 
 * @returns A device's file versions based on its JPOD firmware data.
*/
export const getJpodFirmware = (device: Device, isVbuFwEnabled: boolean = false) => {
  return getDeviceFileVersionsHelper({
    device,
    isPlatformValidCallback : isVbusFirmwarePlatformValid,
    validPlatformIds        : [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware],
    isVbuFwEnabled
  });
};

/** 
 * @returns A device's file versions based on its JPOD config data.
*/
export const getJpodConfig = (device: Device, isVbuFwEnabled: boolean = false) => {
  return getDeviceFileVersionsHelper({
    device,
    isPlatformValidCallback : isJpodConfigFirmwarePlatformValid,
    validPlatformIds        : [PLATFORM_IDS.jpodConfig, PLATFORM_IDS.jpod2Config],
    isVbuFwEnabled
  });
};

/** Given a device, will return current version based on platformID,
    and display EdgeApp Version data
*/
export const getEdgeAppVersion = (device: Device): string => {
  return String(getDeviceFileVersionsHelper({
    device,
    validPlatformIds : [
      PLATFORM_IDS.edgeAppFirmware,
      PLATFORM_IDS.hostedAppFirmware,
      PLATFORM_IDS.hostedAppV2Firmware,
      PLATFORM_IDS.hostedAppV3Firmware,
      PLATFORM_IDS.hostedAppV4Firmware,
      PLATFORM_IDS.hostedAppV5Firmware,
      PLATFORM_IDS.hostedAppV6Firmware
    ]
  }));
};

/**
 * Given a files (current fileHistory), will return scriptName (reportedName) based on the file's platformID.
 * If the requested reportedName is unavailable or not present on the given file it returns an empty string.
 *
 * @param files The files to determine the current fileHistory.
 * @returns A string scriptName.
 */
export const getScriptFileName = (files: GenericObject[]) => {
  const filesWithReportedName = files?.filter(({reportedName}) => !!reportedName);
  const scriptName = filesWithReportedName?.filter(({platformId}) =>
    platformId === PLATFORM_IDS.config ||
    platformId === PLATFORM_IDS.peg2)?.map(item => item?.reportedName)[0];
  return scriptName || '';
};

/**
 * Given a filemeta, will return Script description based on matched platformID,
 * and display Script Description or empty string.
 */
export const getScriptFileDescription = (filemeta: DeviceFiles): string => {
  const ids = [PLATFORM_IDS.config, PLATFORM_IDS.peg2];
  return filemeta?.find(({description, validationMetaData: {platformId} = {}}) =>
    description && ids?.includes(Number(platformId))
  )?.description || '';
};

/**
 * Given a device, will return JPOD/OBD/VBU file version based on device type.
 */
export const getVbusFirmware = (device, platform) => {
  const deviceMode = device?.extensions?.vbusMode;
  if (deviceMode) {
    if (isEqual(deviceMode, VbusFirmwareMode.JPOD)) {
      return platform === PLATFORM_IDS.jpodFirmware || platform === PLATFORM_IDS.jpod2Firmware;
    } else if (isEqual(deviceMode, VbusFirmwareMode.OBD)) {
      return platform === PLATFORM_IDS.obd1Type1Firmware || platform === PLATFORM_IDS.obd2Type2Firmware;
    } else if (isEqual(deviceMode, VbusFirmwareMode.VBU)) {
      return platform === PLATFORM_IDS.vbu3Firmware;
    }
  }
};

/**
 * Given a device, will return JPOD file version based on device type.
 */
export const getJpodConfigFirmware = (device, platform) => {
  const deviceMode = device?.extensions?.vbusMode;
  if (deviceMode) {
    if (isEqual(deviceMode, VbusFirmwareMode.JPOD)) {
      return platform === PLATFORM_IDS.jpodConfig || platform === PLATFORM_IDS.jpod2Config;
    }
  }
};

/**
 * Constructs a string containing all the names of a device's groups separated by a separator.
 * 
 * ```typescript
 * 
 *   import {getDeviceGroupNames} from '@ion/core/src/utils/DeviceUtils';
 * 
 *   // Uses the default separator
 *   const deviceGroupNames = getDeviceGroupNames(device);
 * 
 *   // Uses a specified separator
 *   const deviceGroupNames = getDeviceGroupNames(device, ' & ');
 * 
 * ```
 * 
 * @param   device    Device from which to get group names.
 * @param   separator Placed between each pair of group names. Defaults to
 *          ', ' (comma and space).
 * @returns An empty string if the device has no groups or if anything in the
 *          call chain is undefined.
 */
export const getDeviceGroupNames = (device: Device, separator: string = ', '): string => {
  const deviceGroupTitles = device?.groups?.map?.(({title}) => title);
  return deviceGroupTitles?.join(separator) || '';
};

/**
 * Gets the appropriate iOn tag from a device's data. Takes a single argument, which is the device.
 * 
 * ```typescript
 * 
 *   import {getDeviceIonTag} from '@ion/core/src/utils/DeviceUtils';
 * 
 *   const ionTag = getDeviceIonTag(device);
 * 
 * ```
 * 
 * @param   param0 The device to calculate the tag from.
 * @returns A string containing the iOn tag.
 */
export const getDeviceIonTag = ({architecture, deviceConfiguration}: Device): string => {
  if (architecture === 'SCI') {
    const {title} = deviceConfiguration;
    if (title.startsWith('PTA')) {
      return translate('admin-devices:grid.tags.proxTag');
    } else if (title.endsWith('HT')) {
      return translate('admin-devices:grid.tags.environTag');
    } else if (title.endsWith('XT')) {
      return translate('admin-devices:grid.tags.xtremeTempTag');
    } else if (title.startsWith('SCI_TAG')) {
      return translate('common:booleans.yes');
    } else {
      return translate('common:booleans.no');
    }
  }
  return translate('common:booleans.no');
};

/**
 * Check the search term has asterisk already than
 * normalize and return else simply add the asterisk in the search and return.
 */
export const checkSearchTerm = (searchTerm: string): string => {
  return hasAsterisk.test(searchTerm) ? normalizeAsterisks(searchTerm) : `*${searchTerm}*`;
};

/**
 * Append the OR key word when the search key word has multiple items with comma or space
 * separated and return it.
 */
export const getMultipleSearchKeywords = (searchKeyword: any) => {
  const searchKeywords = searchKeyword?.trim()?.split(/[ ,]+/) || [];
  const multipleSearchTerms = [];
  if (searchKeywords?.length > 1) {
    searchKeywords?.filter((item: any) => {
      if (item!== '') {
        multipleSearchTerms.push(validateQuery(item));
      }
    });
    return multipleSearchTerms.join(' OR ');
  }
  return checkSearchTerm(searchKeyword);
};

/**
 * Define the regex for asterisk is exist in the start and end of the string.
 */
export const hasAsterisk  = /^\*|\*$/;

/**
 * check the given query has asterisk in the
 * begining. Based on the match return the boolean value.
 */
export const isQueryStartWithAsterisk = (query: string): boolean => {
  const hasAsteriskStart = /^\*/;
  return hasAsteriskStart.test(query);
};

/**
 * check the given query has asterisk in the
 * end. Based on the match return the boolean value.
 */
export const isQueryEndsWithAsterisk = (query: string): boolean => {
  const hasAsteriskEnd = /\*$/;
  return hasAsteriskEnd.test(query);
};

/**
 * Check the given string has astrisk in the beginning
 * and end of the string and return the query accordingly.
 */
export const normalizeAsterisks = (query: string): string => {
  const hasAsteriskStartEnd = /^\*.*\*$/;
  const partialQuery        = query.replaceAll('*', '');
  if (hasAsteriskStartEnd.test(query)) {
    return `*${partialQuery}*`;
  } else if (isQueryStartWithAsterisk(query)) {
    return `*${partialQuery}`;
  } else if (isQueryEndsWithAsterisk(query)) {
    return `${partialQuery}*`;
  }
  return query;
};

/**
 * Check the `isAnyField` is true and query has asterisk in the start or end, if exist
 * than call the normalize method, else simply return the same query. Return the partial
 * query by default when the isAnyField is coming as false.
 */
export const validateQuery = (query: string, isAnyField = true, hasExactMatch = false): string => {
  if (isAnyField) {
    return hasAsterisk.test(query) ? normalizeAsterisks(query) : query;
  } if (hasExactMatch) {
    return query;
  }
  return `*${query.replaceAll('*', '')}*`;
};

/**
 * getHeartBeat method to get the Heart Beat data from
 * crashboxx API and return the result on success. Return
 * empty object when server returns any exceptions.
 */
export const getHeartBeat = async(heartBeatPayload: GenericObject, includeSubGroups: boolean) => {
  try {
    const heartBeatInfo = await loadHeartBeatInfo(heartBeatPayload, includeSubGroups);
    return heartBeatInfo;
  } catch (err) {
    error(err);
    return {};
  }
};

/**
 * Check if groups has inheritable property.
 */
export const hasGroupInherited = ({inheritable}): boolean => inheritable || false;

/**
 * Utility to check the query has ends with asterisk(*) and add slashes 
 * and return the query. 
 */
export const addSlashesToQuery = (query: string): string => {
  return isQueryEndsWithAsterisk(query) ? query.replaceAll('*', '\\*') : query;
};

export const getDeviceFilterValue = (value: string): string => {
  if (value.startsWith('-')) {
    return '!(' + value.slice(1) + ')';
  }
  return value;
};
